import { h } from '@atomify/jsx';
import { BoundingBox, MapOptions, MapSearchOptions, MapSearchSuggestion } from '@bpd-library/types';
import {
    calculateBoundingBoxCenter,
    calculateCenter,
    calculateDistanceInKm,
    debounce,
    environmentDetails,
    expandBoundingBox,
    getCountryBoundingBox,
    useClickOutside,
    withinBoundingBox,
} from '@bpd-library/utilities';
import mapboxgl from 'mapbox-gl';

import {
    clearSearchResult,
    getMapboxData,
    MapState,
    mapStore,
    SearchResultPlace,
    updateSearchIsMoving,
    updateSearchResult,
} from '../../../store';
import { fitBounds } from '../../fit-bounds';
import getCityAlternativeSuggestions from './alternative-city-suggestions';

const DEFAULT_TYPES = 'region,district,place,neighborhood';
let defaultKmPadding = 10;

export function setDefaultKmPadding(kmPadding: number) {
    return (defaultKmPadding = kmPadding);
}

const search = async (
    map: mapboxgl.Map,
    {
        hook,
        onSearchSuggestion,
        noZoomAfterSearch,
        distanceFilter,
        types = DEFAULT_TYPES,
    }: MapSearchOptions,
    { searchCountryIsoCodes = ['nl', 'de'], language = 'nl' }: MapOptions,
) => {
    const element = document.querySelector(hook);
    const bounds = getCountryBoundingBox(searchCountryIsoCodes);

    if (!element) {
        if (environmentDetails.isLocal) console.error(`Search element can't be found: ${hook}`);
        return;
    }

    const inputElement = element.querySelector('[js-hook-map-search-input]') as HTMLInputElement;
    const inputSelectDistanceElement = element.querySelector(
        '[js-hook-map-search-distance-select-input]',
    ) as HTMLSelectElement;

    const form = inputElement.closest<HTMLFormElement>('form');
    const suggestionsElement = element.querySelector(
        '[js-hook-map-search-suggestions]',
    ) as HTMLElement;
    const buttonElement = element.querySelector('[js-hook-map-search-button]') as HTMLButtonElement;

    let geocoder: any | undefined;
    let suggestions: any | undefined;

    init();

    async function init() {
        // Dynamically import geocoder library
        const { default: MapboxGeocoder } = await import('@mapbox/mapbox-gl-geocoder');

        geocoder = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl,
            countries: searchCountryIsoCodes.join(',').toUpperCase(),
            types,
            localGeocoder,
            language,
            enableEventLogging: false,
            marker: false,
        });

        map.addControl(geocoder);

        bindEvents();
        subscribe();
    }

    function bindEvents() {
        const debouncedOnKeyDown = debounce(onKeyDown, 300);
        inputElement.addEventListener('keydown', debouncedOnKeyDown);
        buttonElement.addEventListener('click', handleButtonClick);
        form?.addEventListener('submit', (e) => e.preventDefault());

        useClickOutside(inputElement!, handleClickOutside);

        geocoder.on('results', (results: any) => {
            suggestions = results;

            renderSuggestions(suggestions);
        });
    }

    function subscribe() {
        handleManualSearch(mapStore.getState());
        mapStore.subscribe(handleManualSearch, ['manualSearch']);
    }

    function handleManualSearch({ manualSearch }: MapState) {
        if (manualSearch) {
            // If manualSearch is an array, it means coordinates are sent.
            // In that case, join them as a string and send it to the geocoder
            let query: string;

            if (Array.isArray(manualSearch)) {
                geocoder.options.reverseGeocode = true;
                query = manualSearch.join(', ');
            } else {
                geocoder.options.reverseGeocode = false;
                query = manualSearch;
            }

            geocoder.setInput(query).on('results', function handleResults(results: any) {
                suggestions = results;

                let firstExactMatch: any;

                // First check if there's an exact match, in some cases bigger cities that are not an exact match are returned as first and most relevant result, while doing a manual search you always want an exact match.
                if (typeof manualSearch === 'string') {
                    firstExactMatch = results?.features?.find(
                        ({ text }: { text?: string }) =>
                            text?.toLowerCase() === manualSearch.toLowerCase(),
                    );
                }

                if (firstExactMatch) {
                    submitSuggestion(firstExactMatch);
                } else {
                    submitFirstSuggestion();
                }

                geocoder.off('results', handleResults);
            });
        }
    }

    function handleClickOutside() {
        clearSuggestionsContainer();
    }

    function handleFitBounds() {
        if (noZoomAfterSearch) return;

        fitBounds(map, bounds);
    }

    function onKeyDown(e: KeyboardEvent) {
        const { value } = inputElement!;

        if (e.key === 'Enter') {
            handleButtonClick();
        }

        if (value.length >= 3) {
            geocoder.setInput(value);
        } else {
            suggestions = undefined;
            handleFitBounds();
            clearSuggestionsContainer();
            clearSearchResult();
        }
    }

    function handleButtonClick() {
        const { value } = inputElement!;

        if (!value) {
            clearAll();
        } else {
            geocoder.setInput(value).on('results', function handleSubmit(results: any) {
                suggestions = results;
                submitFirstSuggestion();
                inputElement?.blur();

                geocoder.off('results', handleSubmit);
            });
        }
    }

    function submitFirstSuggestion() {
        const firstSuggestion = suggestions?.features[0];

        submitSuggestion(firstSuggestion);
    }

    function submitSuggestion(feature: any) {
        updateStore(feature);
        clearSuggestionsContainer();
    }

    function clearSuggestionsContainer() {
        suggestionsElement.innerHTML = '';
        suggestions = undefined;
    }

    function clearAll() {
        suggestions = undefined;
        handleFitBounds();
        clearSuggestionsContainer();
        clearSearchResult();
        inputElement?.blur();
    }

    function localGeocoder(query: string) {
        const createSuggestion = ({ title, bbox, type, id }: MapSearchSuggestion) => {
            const center = calculateBoundingBoxCenter(bbox);

            return {
                place_name: title,
                bbox,
                center,
                type,
                id,
            };
        };

        const customSuggestions = onSearchSuggestion
            ? onSearchSuggestion(query.toLowerCase()).map(createSuggestion)
            : [];
        const cityAlternatives = getCityAlternativeSuggestions(query.toLowerCase()).map(
            createSuggestion,
        );

        return [...customSuggestions, ...cityAlternatives].slice(0, 4);
    }

    function updateStore(result: any) {
        const kmPadding = !!Number(inputSelectDistanceElement?.value)
            ? Number(inputSelectDistanceElement.value)
            : defaultKmPadding;

        if (!result) {
            updateSearchResult({
                items: [],
                result: false,
            });

            handleFitBounds();

            return;
        }

        /*
            There might not be a bounding box returned.
            If so, generate one based on the center.
        */
        const originalBoundingBox: BoundingBox = result.bbox
            ? result.bbox
            : [result.center[0], result.center[1], result.center[0], result.center[1]];
        const expandedBoundingBox = expandBoundingBox(originalBoundingBox, kmPadding);

        const mapData = getMapboxData();
        const filteredData = mapData.filter((item) => {
            switch (distanceFilter) {
                case 'turfDistance':
                    const itemCenter = calculateCenter(item.coordinates!);
                    const distance = calculateDistanceInKm(result.center, itemCenter);
                    return distance <= kmPadding;

                case 'bbox':
                default:
                    return withinBoundingBox(item.coordinates, expandedBoundingBox);
            }
        });

        if (!noZoomAfterSearch) {
            map.fitBounds(expandedBoundingBox);

            // Update searchIsMoving boolean in store. Is used to block other focus interactions when searched.
            updateSearchIsMoving(true);

            map.once('moveend', () => {
                updateSearchIsMoving(false);
            });
        }

        const place = parseResultText(result);

        updateSearchResult({
            items: filteredData,
            result,
            originalBoundingBox,
            expandedBoundingBox,
            place,
            kmPadding,
        });
    }

    function handleSuggestionClick(feature: any) {
        inputElement.value = feature.place_name;

        clearSuggestionsContainer();
        updateStore(feature);
    }

    function renderSuggestions(suggestions: any) {
        clearSuggestionsContainer();

        suggestionsElement.append(
            <ul class="map-search__suggestions">
                {suggestions.features.map((feature: any) => (
                    <li class="map-search__suggestion">
                        <button onClick={() => handleSuggestionClick(feature)}>
                            {feature.place_name}
                        </button>
                    </li>
                ))}
            </ul>,
        );
    }

    function parseResultText(searchResult: any): SearchResultPlace {
        const text: string = searchResult?.place_name || '';
        const isPoi: boolean = searchResult?.place_type?.includes('poi');

        if (isPoi) return {};

        const [country, province, city, district] = text.split(',').reverse();

        return {
            country,
            province,
            city,
            district,
        };
    }
};

export default search;
